import { Calendar, Flag, InfoCircle } from "@mynaui/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getAmericanoMatches,
  getAmericanoStandings,
  getGroupMatches,
  getGroupStandings,
  getKnockoutMatches,
  getLeagueMatches,
  getLeagueStandings,
  getMexicanoMatches,
  getMexicanoSession,
  getMexicanoStandings,
  getStageDetails,
} from "../../api/tournaments";
import { IMatchDetailDTO } from "../../models/IMatch";
import { ArrowRight, CalendarPlus, Hourglass, Trash, Trophy, Users } from "lucide-react";
import MatchCard from "../../components/MatchCard";
import moment from "moment";
import { ILeagueStanding } from "../../models/ILeague";
import { IGroupStandingList } from "../../models/IGroup";
import { IAmericanoLeaderboardRow, IMexicanoSession } from "../../models/IAmericano";
import MexicanoWaiting from "../../components/MexicanoWaiting";
import StandingsTable from "../../components/StandingsTable";
import SeedingTable from "../../components/SeedingTable";
import LeaderboardTable from "../../components/LeaderboardTable";
import Bracket from "../../components/Bracket";
import { canFinishAmericanoEarly, formatSideName, isAmericanoFormat, isLiveRoundFormat } from "../../utils/methods";
import { rankGroupTeams } from "../../utils/seeding";
import { IMatchFilters, StageDetails } from "../../types/tournament";
import StageDetailDialog from "../../components/StageDetailDialog";
import DeleteStageDialog from "../../components/DeleteStageDialog";
import DeleteScheduleDialog from "../../components/DeleteScheduleDialog";
import CompleteBracketDialog from "../../components/CompleteBracketDialog";
import FinishEarlyDialog from "../../components/FinishEarlyDialog";
import { useAppSelector } from "../../store/store";
import {
  canDeleteSchedule,
  canDeleteStage,
  canCreateSchedule,
  canCompleteBracket,
  canFinishAmericano,
} from "../../utils/authorization";
import { Button, Card, EmptyState, Loader, Menu, PageHeader, Segmented, Select, Tabs, Toolbar } from "../../ui";
import OverviewMatchRow from "./OverviewMatchRow";
import "./styles.scss";

const TournamentStageOverview = () => {
  const navigate = useNavigate();
  const { id, tournamentCategoryId, stageId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useAppSelector((state) => state.auth.currentUser);

  const [selectedVenue, setSelectedVenue] = useState("");
  const [selectedRound, setSelectedRound] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  const [matchLoading, setMatchLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteScheduleLoading, setDeleteScheduleLoading] = useState(false);
  const [completeBracketLoading, setCompleteBracketLoading] = useState(false);
  const [finishEarlyLoading, setFinishEarlyLoading] = useState(false);

  const [deleteStageDialog, setDeleteStageDialog] = useState(false);
  const [deleteScheduleDialog, setDeleteScheduleDialog] = useState(false);
  const [completeBracketDialog, setCompleteBracketDialog] = useState(false);
  const [finishEarlyDialog, setFinishEarlyDialog] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [matches, setMatches] = useState<IMatchDetailDTO[]>([]);
  const [matchFilters, setMatchFilters] = useState<IMatchFilters>();
  const [standings, setStandings] = useState<ILeagueStanding[] | IGroupStandingList[] | IAmericanoLeaderboardRow[]>(
    []
  );
  // Mexicano: set up (players enrolled) before any round exists; rounds are run from the referee portal.
  const [mexicanoSession, setMexicanoSession] = useState<IMexicanoSession | null>(null);
  const [activeMatchTab, setActiveMatchTab] = useState<"upcoming" | "latest">("upcoming");
  const [activeTab, setActiveTab] = useState<"overview" | "matches" | "table">("overview");
  const [stage, setStage] = useState<StageDetails>();

  const isFiltered = selectedVenue !== "" || selectedRound !== "" || selectedTeam !== "";

  // Overall seeding across the groups of this stage: all group winners ranked against each
  // other, then all runners-up, and so on - the order a dependent knockout stage qualifies
  // teams in. Shown live, so it reflects the standings as they are right now.
  const seededTeams = useMemo(
    () => (stage?.formatName === "Group" ? rankGroupTeams(standings as IGroupStandingList[]) : []),
    [stage?.formatName, standings]
  );

  // Americano/Mexicano: the block has ended iff every generated match is completed AND
  // every configured round has been generated (spec §5.3 — the same rule the backend's
  // guarded HasEnded uses; distinct non-null Round values vs americanoRules.roundsCount).
  const isAmericano = isAmericanoFormat(stage?.formatName);
  const americanoRoundsPlayed = new Set(matches.filter((m) => m.round).map((m) => m.round)).size;
  const americanoEnded =
    isAmericano &&
    matches.length > 0 &&
    matches.every((m) => m.isCompleted) &&
    (stage?.americanoRules?.roundsCount ?? 0) > 0 &&
    americanoRoundsPlayed >= (stage?.americanoRules?.roundsCount ?? 0);
  // Americano "Finish early": end a running stage after the rounds that have a scored match.
  const showFinishEarly =
    canFinishAmericanoEarly(stage?.formatName, matches, americanoEnded) && canFinishAmericano(currentUser);
  const unitLabel: "Player" | "Team" = (stage?.americanoRules?.unitSize ?? 1) === 1 ? "Player" : "Team";

  // Check if there are empty teams in first round matches for knockout stages
  const hasEmptyTeamsInFirstRound = () => {
    if (stage?.formatName !== "Knockout" || !matches.length) return false;

    // Get the first round matches (matches with no parent matches - these are the entry points to the bracket)
    const firstRoundMatches = matches.filter((match) => {
      return !match.parentMatch1Id && !match.parentMatch2Id;
    });

    if (firstRoundMatches.length === 0) return false;

    // Check if ALL first round matches have empty team IDs (not just some)
    // Button should only show when bracket needs to be filled for the first time
    const allEmpty = firstRoundMatches.every((match) => !match.homeTeamId || !match.awayTeamId);

    return allEmpty;
  };

  const getStageOverview = async (filters?: {
    round: string | null;
    venue: string | null;
    group: string | null;
    team: string | null;
  }) => {
    try {
      const res = await getStageDetails(id!, stageId!);
      setStage(res.data);
      if (isLiveRoundFormat(res.data.formatName)) {
        setMexicanoSession(await getMexicanoSession(res.data.id).then((r) => r.data).catch(() => null));
      }

      await getFormatMatches(res.data.formatName, res.data.id, filters);
      await getStandings(res.data.formatName, res.data.id);
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setLoading(false);
      setDeleteScheduleLoading(false);
      setCompleteBracketLoading(false);
      setFinishEarlyLoading(false);
    }
  };

  const getStandings = async (blockType: string, blockId: string) => {
    if (blockType === "League") {
      const { data } = await getLeagueStandings(blockId);
      setStandings(data);
    } else if (blockType === "Group") {
      const { data } = await getGroupStandings(blockId);
      setStandings(data);
    } else if (isAmericanoFormat(blockType)) {
      const { data } =
        blockType === "Americano" ? await getAmericanoStandings(blockId) : await getMexicanoStandings(blockId);
      setStandings(data);
    }
  };

  const getFormatMatches = async (
    blockType: string,
    blockId: string,
    filters?: { round: string | null; venue: string | null; group: string | null; team: string | null }
  ) => {
    setMatchLoading(true);

    try {
      if (blockType === "League") {
        const { data } = await getLeagueMatches(blockId, {
          venue: filters?.venue ?? selectedVenue,
          team: filters?.team ?? selectedTeam,
          round: filters?.round ?? selectedRound,
        });
        setMatches(data.matches);
        setMatchFilters(data.filters);
        if (data.matches.filter((x: IMatchDetailDTO) => !x.isCompleted).length > 0) {
          setActiveMatchTab("upcoming");
        } else {
          setActiveMatchTab("latest");
        }
      } else if (blockType === "Group") {
        const { data } = await getGroupMatches(blockId, {
          venue: filters?.venue ?? selectedVenue,
          team: filters?.team ?? selectedTeam,
          round: filters?.round ?? selectedRound,
          group: filters?.group ?? selectedGroup,
        });
        setMatches(data.matches);
        setMatchFilters(data.filters);
        if (data.matches.filter((x: IMatchDetailDTO) => !x.isCompleted).length > 0) {
          setActiveMatchTab("upcoming");
        } else {
          setActiveMatchTab("latest");
        }
      } else if (blockType === "Knockout") {
        const { data } = await getKnockoutMatches(blockId, {
          venue: filters?.venue ?? selectedVenue,
          team: filters?.team ?? selectedTeam,
          round: filters?.round ?? selectedRound,
        });
        setMatches(data.matches);
        setMatchFilters(data.filters);
        if (data.matches.filter((x: IMatchDetailDTO) => !x.isCompleted).length > 0) {
          setActiveMatchTab("upcoming");
        } else {
          setActiveMatchTab("latest");
        }
      } else if (isAmericanoFormat(blockType)) {
        // These endpoints return a bare MatchDTO[] (no server-side filters).
        const { data } =
          blockType === "Americano" ? await getAmericanoMatches(blockId) : await getMexicanoMatches(blockId);
        setMatches(data);
        setMatchFilters({ venues: [], rounds: [], brackets: [], teams: [] });
        if (data.filter((x: IMatchDetailDTO) => !x.isCompleted).length > 0) {
          setActiveMatchTab("upcoming");
        } else {
          setActiveMatchTab("latest");
        }
      }
    } catch (error) {
      console.error("error loading matches");
    } finally {
      setMatchLoading(false);
    }
  };

  const handleTabClick = (tab: "overview" | "matches" | "table") => {
    setActiveTab(tab);
    searchParams.set("tab", tab);
    setSearchParams(searchParams);
  };

  const resetFilters = () => {
    setSelectedRound("");
    setSelectedTeam("");
    setSelectedVenue("");
    setSelectedGroup("");

    const urlParams = new URLSearchParams();
    urlParams.delete("round");
    urlParams.delete("team");
    urlParams.delete("venue");
    urlParams.delete("group");
  };

  useEffect(() => {
    getFormatMatches(stage?.formatName!, stageId!);
  }, [selectedRound, selectedTeam, selectedVenue, selectedGroup]);

  useEffect(() => {
    const tab = searchParams.get("tab") as "overview" | "matches" | "table" | null;
    const round = searchParams.get("round");
    const venue = searchParams.get("venue");
    const group = searchParams.get("group");
    const team = searchParams.get("team");

    if (tab) setActiveTab(tab);

    if (round) setSelectedRound(round);
    if (venue) setSelectedVenue(venue);
    if (group) setSelectedGroup(group);
    if (team) setSelectedTeam(team);

    getStageOverview({ round, venue, group, team });
  }, []);

  const scheduleUrl = `/dashboard/tournaments/${id}/${tournamentCategoryId}/stages/${stageId}/create-schedule?goTo=stage`;
  const tableTabLabel =
    stage?.formatName === "League"
      ? "Table"
      : stage?.formatName === "Group"
      ? "Standings"
      : isAmericano
      ? "Leaderboard"
      : "Bracket";
  const showMoreMenu =
    (matches.length > 0 && canDeleteSchedule(currentUser)) || canDeleteStage(currentUser) || showFinishEarly;

  // The overview's "Upcoming Matches" / "Latest Results" lists: upcoming soonest first, results latest first.
  const overviewMatches =
    activeMatchTab === "upcoming"
      ? matches
          .filter((x) => !x.isCompleted)
          .sort((a, b) => {
            if (!a.startTime && !b.startTime) return 0;
            if (!a.startTime) return 1;
            if (!b.startTime) return -1;
            return moment(a.startTime).diff(moment(b.startTime));
          })
      : matches
          .filter((x) => x.isCompleted)
          .sort((a, b) => {
            if (!a.startTime && !b.startTime) return 0;
            if (!a.startTime) return 1;
            if (!b.startTime) return -1;
            return moment(b.startTime).diff(moment(a.startTime));
          });

  const filterChange = (key: "round" | "team" | "venue" | "group", value: string, set: (v: string) => void) => {
    searchParams.set(key, value);
    setSearchParams(searchParams);
    set(value);
  };

  return (
    <div className="tournament-stage-overview">
      {loading || deleteScheduleLoading || completeBracketLoading || finishEarlyLoading ? (
        <Loader minHeight={480} />
      ) : (
        <>
          <PageHeader
            back={{ onClick: () => navigate(`/dashboard/tournaments/${id}/${tournamentCategoryId}`) }}
            title={
              <>
                {stage?.categoryName} - <span className="stage-overview__alias">{stage?.aliasName || stage?.formatName}</span>
              </>
            }
            subtitle={stage?.tournamentName}
            actions={
              <>
                {stage?.formatName === "Knockout" && hasEmptyTeamsInFirstRound() && canCompleteBracket(currentUser) && (
                  <Button icon={<Trophy />} onClick={() => setCompleteBracketDialog(true)}>
                    Complete Bracket
                  </Button>
                )}
                <Button variant="ghost" icon={<InfoCircle />} onClick={() => setDetailsOpen(true)}>
                  Details
                </Button>
                {showMoreMenu && (
                  <Menu
                    label="More options"
                    items={[
                      {
                        label: "Finish early",
                        icon: <Flag />,
                        visible: showFinishEarly,
                        onClick: () => setFinishEarlyDialog(true),
                      },
                      {
                        label: "Delete schedule",
                        icon: <Calendar />,
                        visible: matches.length > 0 && canDeleteSchedule(currentUser),
                        onClick: () => setDeleteScheduleDialog(true),
                      },
                      {
                        label: "Delete stage",
                        icon: <Trash />,
                        tone: "danger",
                        visible: canDeleteStage(currentUser),
                        onClick: () => setDeleteStageDialog(true),
                      },
                    ]}
                  />
                )}
              </>
            }
          >
            <Tabs
              ariaLabel="Stage sections"
              value={activeTab}
              onChange={(tab) => {
                resetFilters();
                handleTabClick(tab);
              }}
              options={[
                { value: "overview", label: "Overview" },
                { value: "matches", label: "Matches", disabled: matches.length === 0 },
                { value: "table", label: tableTabLabel, disabled: matches.length === 0 },
              ]}
            />
          </PageHeader>
          <div className="main-content">
            {matchLoading ? (
              <Loader minHeight={320} />
            ) : activeTab === "overview" ? (
              <div className="overview-container">
                {stage?.teams && stage?.teams.length > 0 ? (
                  matches.length > 0 ? (
                    <>
                      {matches.every((m) => m.isCompleted) && (
                        <div className="winner-team-container">
                          {stage.formatName === "League" && standings.length > 0 && (
                            <div className="winner-card">
                              <Trophy size={40} className="winner-trophy" />
                              <p className="winner-label">League Winner</p>
                              <p className="winner-name">{(standings as ILeagueStanding[])[0].teamName}</p>
                            </div>
                          )}

                          {stage.formatName === "Group" && (standings as IGroupStandingList[]).length > 0 && (
                            <div className="group-winners">
                              {(standings as IGroupStandingList[]).map((group, idx) => (
                                <div key={idx} className="group-winner">
                                  <Trophy size={40} className="winner-trophy" />
                                  <p className="winner-label">Group {group.groupName} Leader</p>
                                  <p className="winner-name">{group.standings[0].teamName}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {stage.formatName === "Knockout" &&
                            matches.length > 0 &&
                            (() => {
                              const finalMatch = [...matches].filter((m) => m.isCompleted && m.round === "Final")[0];

                              if (!finalMatch || !finalMatch.winnerTeamId) return null;

                              const winnerName =
                                finalMatch.winnerTeamId === finalMatch.homeTeamId
                                  ? finalMatch.homeTeamName
                                  : finalMatch.awayTeamName;

                              return (
                                <div className="winner-card">
                                  <Trophy size={40} className="winner-trophy" />
                                  <p className="winner-label">Winner</p>
                                  <p className="winner-name">{winnerName}</p>
                                </div>
                              );
                            })()}

                          {isAmericano && americanoEnded && standings.length > 0 && (
                            <div className="winner-card">
                              <Trophy size={40} className="winner-trophy" />
                              <p className="winner-label">Winner</p>
                              <p className="winner-name">
                                {(standings as IAmericanoLeaderboardRow[])[0].teamName}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="overview-content">
                        <div className="left-section">
                          <Card
                            className="overview-card"
                            title={
                              stage.formatName === "League"
                                ? "League Table"
                                : stage.formatName === "Group"
                                ? "Standings"
                                : isAmericano
                                ? "Leaderboard"
                                : "Bracket"
                            }
                          >
                            {stage.formatName === "League" ? (
                              <StandingsTable>
                                {(standings as ILeagueStanding[]).map((standing, id) => (
                                  <tr key={id}>
                                    <td>{id + 1}</td>
                                    <td>{standing.teamName}</td>
                                    <td>{standing.played}</td>
                                    <td>{standing.wins}</td>
                                    <td>{standing.losses}</td>
                                    <td>
                                      {standing.setsWon}:{standing.setsLost}
                                    </td>
                                    <td>{standing.pointDifference}</td>
                                    <td>{standing.points}</td>
                                  </tr>
                                ))}
                              </StandingsTable>
                            ) : stage.formatName === "Group" ? (
                              <div className="group-standings">
                                {(standings as IGroupStandingList[]).slice(0, 2).map((standing, id) => (
                                  <div className="group-standing" key={id}>
                                    <p className="group-title">Group {standing.groupName}</p>
                                    <StandingsTable>
                                      {standing.standings.map((standing, id) => (
                                        <tr key={id}>
                                          <td>{id + 1}</td>
                                          <td>{standing.teamName}</td>
                                          <td>{standing.played}</td>
                                          <td>{standing.wins}</td>
                                          <td>{standing.losses}</td>
                                          <td>
                                            {standing.setsWon}:{standing.setsLost}
                                          </td>
                                          <td>{standing.pointDifference}</td>
                                          <td>{standing.points}</td>
                                        </tr>
                                      ))}
                                    </StandingsTable>
                                  </div>
                                ))}
                                <Button variant="link" iconRight={<ArrowRight />} onClick={() => handleTabClick("table")}>
                                  View Standings
                                </Button>
                              </div>
                            ) : stage.formatName === "Knockout" ? (
                              <div className="bracket-main-container">
                                <Bracket matches={matches} />
                              </div>
                            ) : isAmericano ? (
                              <LeaderboardTable
                                rows={standings as IAmericanoLeaderboardRow[]}
                                unitLabel={unitLabel}
                              />
                            ) : null}
                          </Card>
                        </div>
                        <div className="right-section">
                          <Card className="overview-card">
                            <Segmented
                              className="match-tabs-list"
                              ariaLabel="Matches to show"
                              value={activeMatchTab}
                              onChange={setActiveMatchTab}
                              options={[
                                { value: "upcoming", label: "Upcoming Matches" },
                                { value: "latest", label: "Latest Results" },
                              ]}
                            />
                            <div className="matches-list">
                              {overviewMatches.length > 0 ? (
                                overviewMatches
                                  .slice(0, 3)
                                  .map((match, id) => (
                                    <OverviewMatchRow
                                      key={id}
                                      match={match}
                                      onOpen={() => navigate(`/dashboard/tournaments/matches/${match.id}`)}
                                    />
                                  ))
                              ) : (
                                <EmptyState
                                  compact
                                  title="No matches"
                                  description={
                                    activeMatchTab === "upcoming"
                                      ? "There are no upcoming matches"
                                      : "There are no results yet"
                                  }
                                />
                              )}
                            </div>
                            {overviewMatches.length > 0 && (
                              <Button
                                variant="link"
                                block
                                className="view-all-matches-button"
                                iconRight={<ArrowRight />}
                                onClick={() => handleTabClick("matches")}
                              >
                                View All Matches
                              </Button>
                            )}
                          </Card>
                        </div>
                      </div>
                    </>
                  ) : isLiveRoundFormat(stage.formatName) && (mexicanoSession?.players.length ?? 0) > 0 ? (
                    <MexicanoWaiting
                      playerCount={mexicanoSession!.players.length}
                      plannedRounds={mexicanoSession!.plannedRounds}
                      courts={mexicanoSession!.courts}
                      canEditSetup={canCreateSchedule(currentUser)}
                      onEditSetup={() => navigate(scheduleUrl)}
                    />
                  ) : stage.precedingStages.filter((x) => x.stageOrder < stage.order).findIndex((x) => !x.isEnded) !==
                    -1 ? (
                    <Card padding="none">
                      <EmptyState
                        icon={<Hourglass />}
                        title="Waiting for other stages"
                        description="This stage's scheduling is dependent on one or multiple other stage(s). You will be able to schedule this stage's matches once its dependents are complete."
                        action={
                          canCreateSchedule(currentUser) && (
                            <Button iconRight={<ArrowRight />} onClick={() => navigate(scheduleUrl)}>
                              Create Schedule Anyway
                            </Button>
                          )
                        }
                      />
                    </Card>
                  ) : (
                    <Card padding="none">
                      <EmptyState
                        icon={<CalendarPlus />}
                        title="Teams registered"
                        description="Teams have registered for this category. This category is ready for match scheduling"
                        action={
                          canCreateSchedule(currentUser) && (
                            <Button iconRight={<ArrowRight />} onClick={() => navigate(scheduleUrl)}>
                              Create Schedule
                            </Button>
                          )
                        }
                      />
                    </Card>
                  )
                ) : (
                  <Card padding="none">
                    <EmptyState
                      icon={<Users />}
                      title="Waiting for registrations"
                      description="Waiting for teams to register for this category"
                    />
                  </Card>
                )}
              </div>
            ) : activeTab === "matches" ? (
              <div className="matches-container">
                <Toolbar
                  actions={
                    <Button variant="tint" onClick={() => resetFilters()}>
                      Reset Filters
                    </Button>
                  }
                >
                  <Select
                    aria-label="Round"
                    className="stage-overview__filter"
                    value={selectedRound}
                    onChange={(e) => filterChange("round", e.target.value, setSelectedRound)}
                  >
                    <option value={""}>All rounds</option>
                    {matchFilters?.rounds.map((round, id) => (
                      <option value={round} key={id}>
                        {round}
                      </option>
                    ))}
                  </Select>
                  <Select
                    aria-label="Team"
                    className="stage-overview__filter"
                    value={selectedTeam}
                    onChange={(e) => filterChange("team", e.target.value, setSelectedTeam)}
                  >
                    <option value={""}>All teams</option>
                    {matchFilters?.teams.map((team, id) => (
                      <option value={team} key={id}>
                        {team}
                      </option>
                    ))}
                  </Select>
                  <Select
                    aria-label="Venue"
                    className="stage-overview__filter"
                    value={selectedVenue}
                    onChange={(e) => filterChange("venue", e.target.value, setSelectedVenue)}
                  >
                    <option value={""}>All venues</option>
                    {matchFilters?.venues.map((venue, id) => (
                      <option value={venue} key={id}>
                        {venue}
                      </option>
                    ))}
                  </Select>
                  {stage?.formatName === "Group" && (
                    <Select
                      aria-label="Group"
                      className="stage-overview__filter"
                      value={selectedGroup}
                      onChange={(e) => filterChange("group", e.target.value, setSelectedGroup)}
                    >
                      <option value={""}>All Groups</option>
                      {matchFilters?.brackets.map((bracket, id) => (
                        <option value={bracket} key={id}>
                          {bracket}
                        </option>
                      ))}
                    </Select>
                  )}
                </Toolbar>

                <h3 className="upcoming-matches-title">Upcoming Matches</h3>
                <div className="matches-list">
                  {matches.filter((x) => !x.isCompleted).length > 0 ? (
                    matches
                      .filter((x) => !x.isCompleted)
                      .sort((a, b) => {
                        // compare startTime
                        if (!a.startTime && !b.startTime) {
                          // both have no time -> sort by venue
                          return (a.venue || "").localeCompare(b.venue || "");
                        }
                        if (!a.startTime) return 1;
                        if (!b.startTime) return -1;

                        const timeDiff = moment(a.startTime).diff(moment(b.startTime));
                        if (timeDiff !== 0) return timeDiff;

                        // if same time, sort by venue
                        return (a.venue || "").localeCompare(b.venue || "");
                      })
                      .map((match, id) => <MatchCard key={id} match={match} />)
                  ) : (
                    <p className="no-matches-title">No upcoming matches {isFiltered ? "matching your filters" : ""}</p>
                  )}
                </div>

                <h3 className="latest-results-title">Latest Results</h3>
                <div className="matches-list">
                  {matches.filter((x) => x.isCompleted).length > 0 ? (
                    matches
                      .filter((x) => x.isCompleted)
                      .sort((a, b) => {
                        if (!a.startTime && !b.startTime) return 0;
                        if (!a.startTime) return 1;
                        if (!b.startTime) return -1;
                        return moment(b.startTime).diff(moment(a.startTime));
                      })
                      .map((match, id) => <MatchCard key={id} match={match} />)
                  ) : (
                    <p className="no-matches-title">No results {isFiltered ? "matching your filters" : "yet"}</p>
                  )}
                </div>
              </div>
            ) : activeTab === "table" ? (
              <div className="table-container">
                {stage?.formatName === "League" ? (
                  <StandingsTable>
                    {(standings as ILeagueStanding[]).map((standing, id) => (
                      <tr key={id}>
                        <td>{id + 1}</td>
                        <td>{standing.teamName}</td>
                        <td>{standing.played}</td>
                        <td>{standing.wins}</td>
                        <td>{standing.losses}</td>
                        <td>
                          {standing.setsWon}:{standing.setsLost}
                        </td>
                        <td>{standing.pointDifference}</td>
                        <td>{standing.points}</td>
                      </tr>
                    ))}
                  </StandingsTable>
                ) : stage?.formatName === "Group" ? (
                  <div className="group-standings">
                    <div className="group-standing">
                      <p className="group-title">Overall Seeding</p>
                      <p className="seeding-subtitle">
                        Group winners ranked first, then runners-up, and so on - the order teams qualify for a
                        knockout stage in.
                      </p>
                      <SeedingTable teams={seededTeams} />
                    </div>
                    {(standings as IGroupStandingList[]).map((standing, id) => (
                      <div className="group-standing" key={id}>
                        <p className="group-title">Group {standing.groupName}</p>
                        <StandingsTable>
                          {standing.standings.map((standing, id) => (
                            <tr key={id}>
                              <td>{id + 1}</td>
                              <td>{standing.teamName}</td>
                              <td>{standing.played}</td>
                              <td>{standing.wins}</td>
                              <td>{standing.losses}</td>
                              <td>
                                {standing.setsWon}:{standing.setsLost}
                              </td>
                              <td>{standing.pointDifference}</td>
                              <td>{standing.points}</td>
                            </tr>
                          ))}
                        </StandingsTable>
                      </div>
                    ))}
                  </div>
                ) : stage?.formatName === "Knockout" ? (
                  <Card padding="sm" className="bracket-main-container">
                    <Bracket matches={matches} />
                  </Card>
                ) : isAmericano ? (
                  <LeaderboardTable rows={standings as IAmericanoLeaderboardRow[]} unitLabel={unitLabel} />
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      )}
      <StageDetailDialog
        isOpen={detailsOpen}
        stage={stage!}
        onClose={() => setDetailsOpen(false)}
        onConfirm={() => {}}
      />
      <DeleteStageDialog
        isOpen={deleteStageDialog}
        stage={stage!}
        onClose={(update) => {
          if (update) navigate(`/dashboard/tournaments/${id}/${tournamentCategoryId}`);
          setDeleteStageDialog(false);
        }}
      />
      <DeleteScheduleDialog
        stage={stage!}
        isOpen={deleteScheduleDialog}
        onClose={(update) => {
          setDeleteScheduleDialog(false);
          if (update) {
            setDeleteScheduleLoading(true);
            setActiveTab("overview");
            getStageOverview();
          }
        }}
      />
      <FinishEarlyDialog
        stage={stage!}
        matches={matches}
        isOpen={finishEarlyDialog}
        onClose={(update) => {
          setFinishEarlyDialog(false);
          if (update) {
            setFinishEarlyLoading(true);
            setActiveTab("overview");
            getStageOverview();
          }
        }}
      />
      <CompleteBracketDialog
        stage={stage!}
        isOpen={completeBracketDialog}
        onClose={(update) => {
          setCompleteBracketDialog(false);
          if (update) {
            setCompleteBracketLoading(true);
            setActiveTab("overview");
            getStageOverview();
          }
        }}
      />
    </div>
  );
};

export default TournamentStageOverview;
